import { useEffect, useState, useCallback } from "react";
import EditorForTitle from "../tools/EditorForTitle";
import { globalFileServer, globalFileIcon } from "../../../axiosInstance";
import Swal from "sweetalert2";

import {
  getFiltersWithItems,
  createItemInFilter,
  deleteItemInFilter,
  editItemPosition,
  getPresignedUrl,
} from "../../server/Filter";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DEFAULT_FILTERS = {
  brand: [{ title: "hamazon-brand", items: [] }],
  kashrut: [{ title: "hamazon-kashrut", items: [] }],
  "sub-brand": [{ title: "hamazon-sub-brand", items: [] }],
};

const mapTenantKindTitle = {
  brand: "hamazon-brand",
  kashrut: "hamazon-kashrut",
  "sub-brand": "hamazon-sub-brand",
  other: "hamazon-other",
};

function groupByTenantKind(arr) {
  const grouped = arr.reduce((acc, item) => {
    const key = item.tenantKind || "other";
    const title = mapTenantKindTitle[key] || key;
    if (!acc[title]) acc[title] = { title, items: [] };
    acc[title].items.push(item);
    return acc;
  }, {});
  return Object.values(grouped);
}

const Filters = () => {
  const [brand, setBrand] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [brandsRes, kashrutRes, subBrandRes] = await Promise.all([
        getFiltersWithItems("brand"),
        getFiltersWithItems("kashrut"),
        getFiltersWithItems("sub-brand"),
      ]);

      const groupedBrands = groupByTenantKind(brandsRes.data);
      const groupedKashrut = groupByTenantKind(kashrutRes.data);
      const groupedSubBrand = groupByTenantKind(subBrandRes.data);

      setBrand({
        brand: groupedBrands.length ? groupedBrands : DEFAULT_FILTERS.brand,

        kashrut: groupedKashrut.length
          ? groupedKashrut
          : DEFAULT_FILTERS.kashrut,

        "sub-brand": groupedSubBrand.length
          ? groupedSubBrand
          : DEFAULT_FILTERS["sub-brand"],
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const deleteItem = async (item, category) => {
    const parameterId = item?.id || item?.Id;
    const title = category?.title || "brand";

    if (!parameterId) {
      console.error("Missing parameterId", { item });
      return;
    }

    const result = await Swal.fire({
      title: "האם אתה בטוח?",
      text: "לא ניתן לשחזר פריט זה!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#22b09a",
      cancelButtonColor: "#d80028",
      confirmButtonText: "מחק",
      cancelButtonText: "בטל",
    });

    if (result.isConfirmed) {
      try {
        const kind = title.replace("hamazon-", "");
        await deleteItemInFilter({ id: parameterId }, kind);
        await fetchAll();
        Swal.fire("נמחק בהצלחה!", "", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("שגיאה", "מחיקת הפריט נכשלה", "error");
      }
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const { categoryTitle } = active.data.current;
    const kindKey = categoryTitle.split("-").pop();
    const category = brand[kindKey]?.find((cat) => cat.title === categoryTitle);
    if (!category) return;

    const list = category.items;
    const oldIndex = list.findIndex((item) => item.id === active.id);
    const newIndex = list.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...list];
    const [movedItem] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, movedItem);

    setBrand((prev) => ({
      ...prev,
      [kindKey]: prev[kindKey].map((cat) =>
        cat.title === categoryTitle ? { ...cat, items: reordered } : cat,
      ),
    }));

    let position = {};
    if (newIndex === 0) position = { kind: "prepend" };
    else if (newIndex === reordered.length - 1) position = { kind: "append" };
    else
      position = {
        kind: "betweenByRank",
        before: reordered[newIndex - 1].rank,
        after: reordered[newIndex + 1].rank,
      };

    await editItemPosition(categoryTitle, movedItem.id, position);
  };

  const categoryTitlesHebrew = {
    "hamazon-brand": "ספק",
    "hamazon-kashrut": "כשרות",
    "hamazon-sub-brand": "מותג",
    "hamazon-other": "אחר",
  };

  if (!brand || Object.keys(brand).length === 0) return <div>Loading...</div>;

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <div className="filters">
        <h1 className="title-role">סינון</h1>
        <div className="flex-container filter-container">
          {Object.entries(brand).map(([kindKey, categories]) => (
            <div key={kindKey} className="category-group">
              {categories.map((category) => (
                <div key={category.title} className="col-lg-4">
                  <h3 className="category-title">
                    {categoryTitlesHebrew[category.title] || category.title}
                  </h3>

                  <SortableContext
                    items={category.items.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <ul>
                      {category.items?.length > 0 ? (
                        category.items.map((item) => (
                          <SortableItem
                            key={item.id}
                            item={item}
                            category={category}
                            kindKey={kindKey}
                            onDelete={deleteItem}
                          />
                        ))
                      ) : (
                        <li>אין פריטים להצגה</li>
                      )}
                    </ul>
                  </SortableContext>

                  <AddFilterPopup
                    kind={category.title}
                    onAdd={(newItem) => {
                      setBrand((prev) => ({
                        ...prev,
                        [kindKey]: prev[kindKey].map((c) =>
                          c.title === category.title
                            ? { ...c, items: [...c.items, newItem] }
                            : c,
                        ),
                      }));
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </DndContext>
  );
};

export default Filters;

const AddFilterPopup = ({ kind, onAdd }) => {
  const handleAddClick = async () => {
    let selectedFile = null;

    const { value: itemName, isConfirmed } = await Swal.fire({
      title: "הוסף פריט חדש",
      input: "text",
      inputLabel: "שם הפריט",
      showCancelButton: true,
      confirmButtonText: "הוסף",
      cancelButtonText: "בטל",
      preConfirm: (value) => {
        if (!value) {
          Swal.showValidationMessage("יש להזין שם פריט!");
          return null;
        }
        return value;
      },
      didOpen: () => {
        if (kind.includes("kashrut")) {
          const container = Swal.getHtmlContainer();
          if (!container) return;

          const fileInput = document.createElement("input");
          fileInput.type = "file";
          fileInput.style.display = "block";
          fileInput.style.marginTop = "10px";

          fileInput.onchange = (e) => {
            selectedFile = e.target.files?.[0] ?? null;
          };

          container.appendChild(fileInput);
        }
      },
    });

    if (!isConfirmed) return;

    const payload = { name: itemName };

    if (kind.includes("kashrut") && selectedFile) {
      const presignRes = await getPresignedUrl(selectedFile, "kashrut-images");

      const uploadUrl = presignRes.data.uploadUrl;

      const fullurl = await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });

      console.log(fullurl);
      const url = fullurl.url;
      const lastPart = url.split("/").pop();

      const fileName = lastPart.split("?")[0];

      payload.img = fileName;
    }

    try {
      const res = await createItemInFilter(kind, payload);
      if (onAdd) onAdd(res.data);

      Swal.fire("נוסף בהצלחה!", "", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("שגיאה", "הוספת הפריט נכשלה", "error");
    }
  };

  return (
    <button className="btn" onClick={handleAddClick}>
      הוסף פריט
    </button>
  );
};

const SortableItem = ({ item, category, kindKey, onDelete }) => {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({
      id: item.id,
      data: { kindKey, categoryTitle: category.title },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div className="sub-title">
        <div className="arrow" {...listeners} {...attributes}>
          <img
            loading="lazy"
            src={globalFileServer + "iconcss/back.svg"}
            alt=""
          />
        </div>

        <EditorForTitle
          kind={category.title}
          id={item.id}
          element={item}
          name="name"
          dist="filters"
        />

        <div
          className="delete-item"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item, category);
          }}
        >
          <div className="img">
            <img loading="lazy" src={globalFileIcon + "trash.svg"} alt="" />
          </div>
        </div>
      </div>
    </li>
  );
};
